import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Container, Col, Row } from 'reactstrap';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz, colorSchemeDark, colorSchemeLight } from 'ag-grid-community';
import config from '../../../../../config';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';
import DateRangePicker from '../../../../../shared/components/widgets/DateRangePicker';
import { formatNumberDefaultToZero } from '../../../../../shared/components/table/functions';

export default function IpFrequencyReportLayout({ title }) {
  const gridRef = useRef();
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const [apiLoad, setApiLoad] = useState(false);
  const [startDate, setStartDate] = useState(moment().subtract(9, 'days')); // moment('20231005', 'YYYYMMDD')
  const [endDate, setEndDate] = useState(moment().subtract(2, 'days'));

  const [apiData, setApiData] = useState([]);

  useEffect(() => {
    setApiLoad(true);
    // setGraphData([{}]);
    setApiData([{}]);

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    const reportUrl = `${config.serverUrl}/amc/ip-frequency-report?accountId=${sellerSelect.value}&startDate=${startDate.format('YYYYMMDD')}&endDate=${endDate.format('YYYYMMDD')}`;
    fetch(reportUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then((data) => {
        setApiData(data);
        setApiLoad(false);
      }).catch(() => {
        setApiLoad(false);
      });
  }, [sellerSelect.value, startDate, endDate]);

  function buildMasters(rows) {
    const toNum = (v) => (v == null ? 0 : Number(v) || 0);
    // const safeDiv = (n, d) => (d ? n / d : null);

    const source = Array.isArray(rows) ? rows : [];
    const byKey = new Map();

    for (let i = 0; i < source.length; i += 1) {
      const r = source[i] || {};
      const key = `${String(r.frequency_bucket)}`;

      let group = byKey.get(key);
      if (!group) {
        group = {
          frequency_bucket: r.frequency_bucket || null,

          user_reach: 0,
          ip_reach: 0,
          total_reach: 0,
          null_impressions: 0,

          daily: [],
        };
        byKey.set(key, group);
      }

      group.user_reach += toNum(r.user_reach);
      group.ip_reach += toNum(r.ip_reach);
      group.total_reach += toNum(r.total_reach);
      group.null_impressions += toNum(r.null_impressions);

      group.daily.push(r);
    }

    const masters = Array.from(byKey.values());

    for (let i = 0; i < masters.length; i += 1) {
      const g = masters[i];

      g.daily.sort((a, b) => {
        const da = a && a.data_date ? String(a.data_date) : '';
        const db = b && b.data_date ? String(b.data_date) : '';
        if (da < db) return -1;
        if (da > db) return 1;
        return 0;
      });
    }

    masters.sort((a, b) => {
      const aNum = parseInt(a.frequency_bucket, 10);
      const bNum = parseInt(b.frequency_bucket, 10);
      if (aNum < bNum) return -1;
      if (aNum > bNum) return 1;
      return 0;
    });

    return masters;
  }

  const masters = useMemo(
    () => buildMasters(apiData || []),
    [apiData],
  );

  const masterCols = useMemo(() => [
    { field: 'frequency_bucket', headerName: 'IpFrequency Bucket', cellRenderer: 'agGroupCellRenderer', pinned: 'left' },
    { field: 'user_reach', headerName: 'User Reach', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'ip_reach', headerName: 'IP Reach', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'total_reach', headerName: 'Total Reach', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'null_impressions', headerName: 'Null Impressions', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
  ], []);


  const detailCellRendererParams = useMemo(() => ({
    detailGridOptions: {
      columnDefs: [
        { field: 'data_date', headerName: 'Date' },
        { field: 'user_reach', headerName: 'User Reach', valueFormatter: formatNumberDefaultToZero },
        { field: 'ip_reach', headerName: 'IP Reach', valueFormatter: formatNumberDefaultToZero },
        { field: 'total_reach', headerName: 'Total Reach', valueFormatter: formatNumberDefaultToZero },
        { field: 'null_impressions', headerName: 'Null Impressions', valueFormatter: formatNumberDefaultToZero },
      ],
      defaultColDef: { flex: 1, sortable: true, resizable: true },
    },
    getDetailRowData: (params) => {
      params.successCallback(params.data.daily || []);
    },
  }), []);

  function dateChange(start, end) {
    setStartDate(moment(start));
    setEndDate(moment(end));
  }

  // Table Settings
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    flex: 1,
  }), []);

  const onGridReady = useCallback(() => {
    gridRef.current.api.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }, []);

  const isDarkMode = theme.className && !theme.className.endsWith('light');
  const useTheme = isDarkMode
    ? themeQuartz.withPart(colorSchemeDark)
    : themeQuartz.withPart(colorSchemeLight);

  const onBtnExport = () => {
    const fileName = `${sellerSelect.value}-AMC-IpFrequency-${moment(startDate).format('YYYYMMDD')}-${moment(endDate).format('YYYYMMDD')}.csv`;
    gridRef.current.api.exportDataAsCsv({ fileName });
  };

  return (
    <Container className="dashboard">
      <Row>
        <Col>
          <CardPanel
            lg={12}
            md={12}
            title=""
            subhead=""
            showButtons={false}
            parentRefresh={apiLoad}
          >
            <Row>
              <Col md={12}>
                <h3 className="page-title">{title}</h3>
              </Col>
            </Row>
            <Row xs={1} md={2} className="align-items-center g-3 p-3 p-md-0">
              {/* left col: center on mobile, normal on md+ */}
              <Col className="d-flex justify-content-center justify-content-md-start text-center text-md-start">
                <DateRangePicker
                  startDate={startDate.toDate()}
                  endDate={endDate.toDate()}
                  onDateChange={dateChange}
                />
              </Col>

              {/* right col: add top padding only when stacked, center on mobile, right on md+ */}
              <Col className="d-flex justify-content-center justify-content-md-end pt-3 pt-md-0">
                <button
                  className="btn btn-outline-primary"
                  style={{ width: 200 }}
                  onClick={onBtnExport}
                  type="button"
                >
                  Export
                </button>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className={useTheme} style={{ height: '50vh', width: '100%' }}>
                  <AgGridReact
                    theme={useTheme}
                    ref={gridRef}
                    rowData={masters}
                    columnDefs={masterCols}
                    defaultColDef={defaultColDef}
                    masterDetail
                    isRowMaster={(dataItem) => Array.isArray(dataItem?.daily) && dataItem.daily.length > 0}
                    detailCellRendererParams={detailCellRendererParams}
                    detailRowAutoHeight
                    groupSuppressAutoColumn={false}
                    animateRows
                    groupIncludeTotalFooter
                    onFirstDataRendered={(p) => p.api.forEachNode(n => n.setExpanded(false))}
                    onGridReady={onGridReady}
                  />
                </div>
              </Col>
            </Row>
          </CardPanel>
        </Col>
      </Row>
    </Container>
  );
}
